/**
 * A per-tab identity for this running copy of the app.
 *
 * Realtime handlers need to answer "did *this* tab cause the event I just
 * received?" so they can skip redundant work on their own echo. User id can't
 * answer it: one manager with the setup sheet open on the office desktop and on
 * an iPad is a single user, so filtering on user id makes each device discard
 * the other's changes and then write from stale data.
 *
 * The id lives in module scope, so it is stable for the lifetime of the page and
 * distinct per tab (a reload is a new instance, which is fine — the old one is
 * gone). Send it on mutations; the server echoes it back on the broadcast.
 */

const generateId = (): string => {
  // randomUUID needs a secure context, which excludes plain-http LAN testing.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `ci-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
}

export const CLIENT_INSTANCE_ID = generateId()

/** Header the server reads to attribute a change back to the originating tab. */
export const CLIENT_INSTANCE_HEADER = 'X-Client-Instance'

/** Merge the client-instance header into a fetch/axios headers object. */
export const withClientInstance = (
  headers: Record<string, string> = {}
): Record<string, string> => ({
  ...headers,
  [CLIENT_INSTANCE_HEADER]: CLIENT_INSTANCE_ID,
})
